import { accountingExportErrorCodes } from '../Types/accountingExportTypes';
import type {
  AccountingExportDocumentItem,
  AccountingExportErrorCode,
  AccountingExportFileQuery,
  AccountingExportItemsQuery,
  AccountingExportProblemDetails,
} from '../Types/accountingExportTypes';
import { TagTypes } from '../../../Api/Types/tagTypes';

export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ACCOUNTING_EXPORT_TAG = TagTypes.AccountingExport;
export const ACCOUNTING_EXPORT_LIST_TAG_ID = TagTypes.List;

export type AccountingExportTag = {
  type: typeof ACCOUNTING_EXPORT_TAG;
  id: typeof ACCOUNTING_EXPORT_LIST_TAG_ID;
};

export type AccountingExportApiErrorResponse = string | AccountingExportProblemDetails;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseAccountingExportError = async (
  response: Response,
  fallbackMessage: string
): Promise<AccountingExportApiErrorResponse> => {
  const contentType = response.headers.get('content-type') ?? '';
  const rawBody = await response.text();
  const trimmedRawBody = rawBody.trim();

  if (trimmedRawBody.length === 0) {
    return fallbackMessage;
  }

  const looksLikeJson =
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json') ||
    trimmedRawBody.startsWith('{') ||
    trimmedRawBody.startsWith('[');

  if (!looksLikeJson) {
    return rawBody;
  }

  try {
    const parsedBody: unknown = JSON.parse(rawBody);
    return isRecord(parsedBody) ? (parsedBody as AccountingExportProblemDetails) : rawBody;
  } catch {
    return rawBody;
  }
};

const buildAccountingExportFileRequest = (
  url: string,
  accept: string,
  fallbackMessage: string,
  query: AccountingExportFileQuery
): {
  url: string;
  method: 'GET';
  params: Record<string, string | number>;
  headers: Record<string, string>;
  responseHandler: (response: Response) => Promise<Blob | AccountingExportApiErrorResponse>;
} => ({
  url,
  method: 'GET',
  params: {
    companyId: query.companyId,
    dateFrom: query.dateFrom,
    dateTo: query.dateTo,
  },
  headers: {
    accept,
  },
  responseHandler: async (response: Response) => {
    if (!response.ok) {
      return parseAccountingExportError(response, fallbackMessage);
    }

    return response.blob();
  },
});

const hasDateValue = (value: string | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

// Mirrors server-side rule: GetAccountingExportItemsQuery.RequiresDateRange in TravelCRM.Application.
// Both locations must stay in sync when the rule changes.
export const requiresDateRangeForAccountingExportItems = (
  query: AccountingExportItemsQuery
): boolean => {
  const source = query.source ?? 'all';
  const channel = query.channel;

  const includesStandaloneSales =
    (source === 'all' || source === 'standalone') && (channel === undefined || channel === 'sales');

  return includesStandaloneSales;
};

export const validateAccountingExportItemsQuery = (query: AccountingExportItemsQuery): void => {
  if (
    requiresDateRangeForAccountingExportItems(query) &&
    (!hasDateValue(query.dateFrom) || !hasDateValue(query.dateTo))
  ) {
    throw new Error(
      "Accounting exports items query requires both 'dateFrom' and 'dateTo' for standalone sales scope."
    );
  }
};

export const buildAccountingExportItemsRequest = (
  query: AccountingExportItemsQuery
): {
  url: string;
  params: Record<string, string | number | boolean | undefined>;
} => {
  validateAccountingExportItemsQuery(query);

  return {
    url: '/accounting-exports/items',
    params: {
      companyId: query.companyId,
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      source: query.source,
      channel: query.channel,
      sentToAccounting: query.sentToAccounting,
    },
  };
};

const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;

const normalizeNullableString = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizeNullableDateOnly = (
  value: string | null | undefined,
  fieldName: 'bookingDateFrom' | 'bookingDateTo',
  source: string,
  documentId: string
): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  if (normalized.length === 0) {
    return null;
  }

  if (!dateOnlyRegex.test(normalized)) {
    console.error(
      `Accounting export items response contains invalid ${fieldName} format (expected YYYY-MM-DD), got "${normalized}" for ${source}:${documentId}.`
    );
    return null;
  }

  return normalized;
};

const mapBookingContextForExportItem = (
  item: AccountingExportDocumentItem
): AccountingExportDocumentItem => {
  const offerName = normalizeNullableString(item.offerName);
  const bookingDateFrom = normalizeNullableDateOnly(
    item.bookingDateFrom,
    'bookingDateFrom',
    item.source,
    item.documentId
  );
  const bookingDateTo = normalizeNullableDateOnly(
    item.bookingDateTo,
    'bookingDateTo',
    item.source,
    item.documentId
  );
  const bookingContextLabel = normalizeNullableString(item.bookingContextLabel);

  if (item.source === 'BookingSnapshot') {
    if (!offerName || !bookingDateFrom || !bookingDateTo) {
      console.error(
        `Accounting export items response is missing booking context for BookingSnapshot:${item.documentId}.`
      );
    }

    return {
      ...item,
      offerName,
      bookingDateFrom,
      bookingDateTo,
      bookingContextLabel,
    };
  }

  if (offerName || bookingDateFrom || bookingDateTo || bookingContextLabel) {
    console.warn(
      `Accounting export items response contains booking context for standalone source ${item.source}:${item.documentId}.`
    );
  }

  return {
    ...item,
    offerName: null,
    bookingDateFrom: null,
    bookingDateTo: null,
    bookingContextLabel: null,
  };
};

export const mapAccountingExportItemsResponse = (
  response: AccountingExportDocumentItem[]
): AccountingExportDocumentItem[] => response.map(mapBookingContextForExportItem);

export const buildAccountingSalesJpkFa4ExportRequest = (query: AccountingExportFileQuery) =>
  buildAccountingExportFileRequest(
    '/accounting-exports/sales/jpk-fa4',
    'application/xml, text/xml;q=0.9, */*;q=0.8',
    'Nie udało się wygenerować eksportu sprzedaży JPK_FA(4).',
    query
  );

export const buildAccountingSalesEdiExportRequest = (query: AccountingExportFileQuery) =>
  buildAccountingExportFileRequest(
    '/accounting-exports/sales/edi',
    'text/plain, application/octet-stream;q=0.9, */*;q=0.8',
    'Nie udało się wygenerować eksportu sprzedaży EDI++.',
    query
  );

export const buildAccountingPurchasesEdiExportRequest = (query: AccountingExportFileQuery) =>
  buildAccountingExportFileRequest(
    '/accounting-exports/purchases/edi',
    'text/plain, application/octet-stream;q=0.9, */*;q=0.8',
    'Nie udało się wygenerować eksportu kosztów EDI++.',
    query
  );

export const buildAccountingSalesNexoXmlExportRequest = (query: AccountingExportFileQuery) =>
  buildAccountingExportFileRequest(
    '/accounting-exports/sales/nexo-xml',
    'application/xml, */*;q=0.8',
    'Nie udało się wygenerować eksportu sprzedaży nexo XML.',
    query
  );

export const buildAccountingPurchasesNexoXmlExportRequest = (query: AccountingExportFileQuery) =>
  buildAccountingExportFileRequest(
    '/accounting-exports/purchases/nexo-xml',
    'application/xml, */*;q=0.8',
    'Nie udało się wygenerować eksportu zakupów nexo XML.',
    query
  );

export const getAccountingExportItemsProvidesTags = (): AccountingExportTag[] => [
  { type: ACCOUNTING_EXPORT_TAG, id: ACCOUNTING_EXPORT_LIST_TAG_ID },
];

export const getAccountingExportItemsInvalidatesTags = (): AccountingExportTag[] => [
  { type: ACCOUNTING_EXPORT_TAG, id: ACCOUNTING_EXPORT_LIST_TAG_ID },
];

export const isAccountingExportErrorCode = (value: string): value is AccountingExportErrorCode =>
  (accountingExportErrorCodes as readonly string[]).includes(value);

export const resolveAccountingExportErrorCode = (
  error: AccountingExportApiErrorResponse | null | undefined
): AccountingExportErrorCode | null => {
  if (!error || typeof error === 'string') {
    return null;
  }

  const candidateCode = error.errorCode ?? error.code;
  if (typeof candidateCode !== 'string') {
    return null;
  }

  return isAccountingExportErrorCode(candidateCode) ? candidateCode : null;
};

